import {
  forwardRef,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from './utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
}

interface ButtonAsButton extends ButtonBaseProps,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  as?: 'button';
  href?: never;
  to?: never;
}

interface ButtonAsAnchor extends ButtonBaseProps,
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> {
  as: 'a';
  href: string;
  to?: never;
}

interface ButtonAsLink extends ButtonBaseProps,
  Omit<LinkProps, keyof ButtonBaseProps | 'className'> {
  as: 'link';
  to: string;
  href?: never;
}

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-primary hover:bg-accent-hover',
  secondary: 'border border-neutral-300 text-neutral-600 hover:bg-neutral-50',
  ghost: 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50',
  destructive: 'border border-error-border text-error-fg hover:bg-error-bg',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-8 py-3.5 text-sm uppercase tracking-wide',
};

const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      className = '',
      children,
      ...rest
    } = props;

    const combinedClassName = cn(
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      fullWidth && 'w-full',
      className,
    );

    const spinnerEl = (
      <span
        aria-hidden="true"
        className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"
      />
    );
    const showLeft = isLoading || Boolean(leftIcon);
    const showRight = !isLoading && Boolean(rightIcon);
    const leftSlot = isLoading ? spinnerEl : leftIcon;
    const gap = children ? 'gap-2' : '';

    const content =
      showLeft || showRight ? (
        <span className={`inline-flex items-center ${gap}`.trim()}>
          {showLeft && (
            <span className="inline-flex" aria-hidden="true">
              {leftSlot}
            </span>
          )}
          {children}
          {showRight && (
            <span className="inline-flex" aria-hidden="true">
              {rightIcon}
            </span>
          )}
        </span>
      ) : (
        children
      );

    if (rest.as === 'a') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { as: _, ...anchorProps } = rest as ButtonAsAnchor;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={combinedClassName}
          {...anchorProps}
        >
          {content}
        </a>
      );
    }

    if (rest.as === 'link') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { as: _, to, ...linkProps } = rest as ButtonAsLink;
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={combinedClassName}
          {...linkProps}
        >
          {content}
        </Link>
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { as: _, disabled, ...buttonProps } = rest as ButtonAsButton;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={combinedClassName}
        {...buttonProps}
        // After the spread, with the caller's `disabled` pulled out of it: loading
        // always implies disabled. A caller may add `disabled` but never remove it.
        disabled={isLoading || disabled}
      >
        {content}
      </button>
    );
  }
);
